import type { ParseKeys } from 'i18next';
import type { ReactNode } from 'react';
import {
  TbBell,
  TbCalendarClock,
  TbChartHistogram,
  TbChecklist,
  TbCoins,
  TbFileInvoice,
  TbGauge,
  TbLifebuoy,
  TbQrcode,
  TbReceipt2,
} from 'react-icons/tb';
import type { FeatureKey } from '@/auth/capabilities';
import type { AppRoute } from '@/types';

type NavItemId =
  | 'cpo-analytics'
  | 'qr-payment'
  | 'alerts'
  | 'session-schedules'
  | 'checklist'
  | 'helpdesk'
  | 'split-billing'
  | 'invoices'
  | 'fees'
  | 'oda';

interface NavItemConfig {
  id: NavItemId;
  icon: ReactNode;
  /** `common`-namespace i18n key of the item's label. */
  labelKey: ParseKeys<'common'>;
  href?: AppRoute;
  /** Rendered only for domain-level IAM callers (e.g. ODA — its data is global). */
  domainOnly?: boolean;
  /**
   * Rendered only for callers the backend grants this feature to (`/api/me/capabilities`).
   * UI convenience — the module's endpoints enforce the same gate with 403s.
   */
  requiresFeature?: FeatureKey;
}

export interface NavSectionConfig {
  /** `common`-namespace i18n key of the section's label. */
  labelKey: ParseKeys<'common'>;
  items: NavItemConfig[];
}

export const NAV_ITEMS: NavSectionConfig[] = [
  {
    labelKey: 'nav.sections.cpms',
    items: [
      {
        id: 'cpo-analytics',
        icon: <TbChartHistogram />,
        labelKey: 'nav.cpoAnalytics',
        href: '/cpo-analytics',
      },
      { id: 'qr-payment', icon: <TbQrcode />, labelKey: 'nav.qrPayment', href: '/qr-payment' },
      { id: 'alerts', icon: <TbBell />, labelKey: 'nav.alerts', href: '/alerts' },
      {
        // Timed remote start/stop of sessions; gated so an environment can switch it
        // off without a deploy (no row = open to everyone).
        id: 'session-schedules',
        icon: <TbCalendarClock />,
        labelKey: 'nav.sessionSchedules',
        href: '/session-schedules',
        requiresFeature: 'session-schedules',
      },
    ],
  },
  {
    labelKey: 'nav.sections.energy',
    items: [
      {
        id: 'oda',
        icon: <TbGauge />,
        labelKey: 'nav.oda',
        href: '/oda',
        domainOnly: true,
        requiresFeature: 'oda',
      },
    ],
  },
  {
    labelKey: 'nav.sections.support',
    items: [
      {
        id: 'helpdesk',
        icon: <TbLifebuoy />,
        labelKey: 'nav.helpdesk',
        href: '/helpdesk',
        requiresFeature: 'helpdesk',
      },
    ],
  },
  {
    labelKey: 'nav.sections.billing',
    items: [
      {
        id: 'split-billing',
        icon: <TbReceipt2 />,
        labelKey: 'nav.splitBilling',
        href: '/billing/split-billing',
      },
      {
        id: 'invoices',
        icon: <TbFileInvoice />,
        labelKey: 'nav.invoices',
        href: '/billing/invoices',
        requiresFeature: 'invoices',
      },
      {
        // The accountant's monthly checks over the whole fleet (CPMS + IAM changes,
        // misconfigured assets). Fleet-wide by nature, so domain callers only.
        id: 'checklist',
        icon: <TbChecklist />,
        labelKey: 'nav.checklist',
        href: '/billing/checklist',
        domainOnly: true,
      },
      {
        // Both fees are what NRG earns; the API refuses the writes to anyone else.
        id: 'fees',
        icon: <TbCoins />,
        labelKey: 'nav.fees',
        href: '/billing/fees',
        domainOnly: true,
      },
    ],
  },
];
